import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ClientPortal from "@/pages/ClientPortal";
import StartNew from "@/pages/StartNew";
import IntakeWizard from "@/pages/IntakeWizard";
import TrustPreview from "@/pages/TrustPreview";
import TrustFinal from "@/pages/TrustFinal";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCaseDetail from "@/pages/AdminCaseDetail";
import AdminTrustViewerPreview from "@/pages/AdminTrustViewerPreview";
import BrandingPage from "@/pages/BrandingPage";
import AcceptInvite from "@/pages/AcceptInvite";
import AdminQuestions from "@/pages/AdminQuestions";
import AdminWhatsAppInbox from "@/pages/AdminWhatsAppInbox";
import AdminRepsPage from "@/pages/AdminRepsPage";
import AdminCommissionsPage from "@/pages/AdminCommissionsPage";
import AccountSettings from "@/pages/AccountSettings";
import ConfirmEmailChange from "@/pages/ConfirmEmailChange";
import ForgotEmail from "@/pages/ForgotEmail";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Company from "@/pages/Company";
import ProtectedRoute from "@/components/ProtectedRoute";

function Root() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  return user.role === "admin" ? <Navigate to="/admin" replace /> : <Navigate to="/portal" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/forgot-email" element={<ForgotEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirm-email" element={<ConfirmEmailChange />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />

            {/* Public A2P / compliance landing page — no auth */}
            <Route path="/company" element={<Company />} />
            <Route path="/about" element={<Company />} />
            <Route path="/privacy" element={<Company />} />
            <Route path="/terms" element={<Company />} />
            <Route path="/sms-terms" element={<Company />} />

            <Route path="/portal" element={<ProtectedRoute><ClientPortal /></ProtectedRoute>} />
            <Route path="/portal/new" element={<ProtectedRoute><StartNew /></ProtectedRoute>} />
            <Route path="/portal/trust-preview/:caseId" element={<ProtectedRoute><TrustPreview /></ProtectedRoute>} />
            <Route path="/portal/trust-final/:caseId" element={<ProtectedRoute><TrustFinal /></ProtectedRoute>} />
            <Route path="/intake/:caseId" element={<ProtectedRoute><IntakeWizard /></ProtectedRoute>} />

            {/* Stripe checkout landings — public so the poll works even if the
                auth cookie was dropped during the cross-origin Stripe roundtrip.
                The status endpoint is unauth by design (only session_id is needed). */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/questions" element={<ProtectedRoute adminOnly><AdminQuestions /></ProtectedRoute>} />
            <Route path="/admin/whatsapp-inbox" element={<ProtectedRoute adminOnly><AdminWhatsAppInbox /></ProtectedRoute>} />
            <Route path="/admin/reps" element={<ProtectedRoute adminOnly><AdminRepsPage /></ProtectedRoute>} />
            <Route path="/admin/commissions" element={<ProtectedRoute adminOnly><AdminCommissionsPage /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
            <Route path="/admin/cases/:caseId" element={<ProtectedRoute adminOnly><AdminCaseDetail /></ProtectedRoute>} />
            <Route path="/admin/cases/:caseId/trust-viewer-preview" element={<ProtectedRoute adminOnly><AdminTrustViewerPreview /></ProtectedRoute>} />
            <Route path="/admin/branding" element={<ProtectedRoute adminOnly><BrandingPage /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
